/**
 * Кадр карты как внешний сигнал.
 *
 * Почему не в `ViewContext`. Регион карты там намеренно живёт в `ref`: контекст
 * стоит НАД маршрутами, и состояние в нём означало бы перерисовку всего
 * кабинета на каждый пан. Но списку видов кадр нужен именно как значение —
 * иначе он не обновится. Отсюда отдельное хранилище: будятся только те, кто
 * подписался (селектор вида), а не всё дерево.
 *
 * Два правила, и оба против лишних перерисовок:
 *  - холостое обновление (тот же кадр) никого не будит — смена настроек и
 *    resize сыплют `moveend` без единого сдвига карты;
 *  - снимок при этом остаётся ТЕМ ЖЕ объектом. `useSyncExternalStore` сравнивает
 *    снимки по ссылке, и новый объект с теми же числами — это бесконечный цикл
 *    перерисовки, а не «ничего не изменилось».
 */

import type { MapBBox } from '../map/MapCanvas'

let current: MapBBox | null = null
const listeners = new Set<() => void>()

function same(a: MapBBox | null, b: MapBBox | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.lon_min === b.lon_min && a.lon_max === b.lon_max
    && a.lat_min === b.lat_min && a.lat_max === b.lat_max
}

/** Кадр сдвинулся — сообщить подписчикам. Холостой вызов игнорируется. */
export function setViewBBox(next: MapBBox): void {
  if (same(current, next)) return
  current = next
  listeners.forEach(fn => fn())
}

export function getViewBBox(): MapBBox | null {
  return current
}

export function subscribeViewBBox(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Только для тестов: хранилище живёт на модуле и переживает их иначе. */
export function resetViewBBox(): void {
  current = null
  listeners.clear()
}
